import { Button, ListItemLayout, Loading, LuneThemeNew } from '@lune-fe/lune-ui-lib'
import Container from '@mui/material/Container'
import { styled } from '@mui/system'
import { useSnackbar } from 'notistack'
import { FC, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import ConditionalTooltip from 'components/ConditionalTooltip'
import NotAdminTooltip from 'components/NotAdminTooltip'
import { uploadOrganisationLogo } from 'endpoints/dapi'
import useActiveOrganisation from 'hooks/useActiveOrganisation'
import useMixpanel from 'hooks/useMixpanel'
import useUserState, { useIsAuthorised } from 'hooks/useUserState'
import { IAccount } from 'models/account'
import { SnackbarMessages } from 'SnackbarMessages'

const Image = styled('img')(({ theme }) =>
    theme.unstable_sx({
        width: `48px`,
        height: `48px`,
        objectFit: 'cover',
        borderRadius: '50%',
    }),
)

const ManageLogo: FC = () => {
    const isAuthorised = useIsAuthorised()
    const [uploading, setUploading] = useState(false)
    const { userState, setUserState } = useUserState()
    const { activeOrg, refetchActiveOrg } = useActiveOrganisation()
    const { enqueueSnackbar: snackbar } = useSnackbar()
    const mixpanel = useMixpanel()
    const { palette } = LuneThemeNew

    const onUploadLogo = (acceptedFiles: File[]) => {
        const formData = new FormData()
        const profilePic = acceptedFiles[0]
        formData.append('logo', profilePic)
        setUploading(true)
        uploadOrganisationLogo(activeOrg?.id!, formData)
            .then((res) => {
                if (userState?.account) {
                    const updatedAccount = {
                        ...userState.account,
                        logo: res.data.url,
                    } as IAccount
                    setUserState({ ...userState, account: updatedAccount })
                }
                snackbar(SnackbarMessages.ORGANISATION_LOGO_UPLOAD_SUCCESS)
                mixpanel.track('settings_logo_saved')
                return refetchActiveOrg()
            })
            .catch(() => {
                snackbar(SnackbarMessages.ORGANISATION_LOGO_UPLOAD_FAIL)
            })
            .finally(() => setUploading(false))
    }

    const Placeholder = useMemo(
        () =>
            styled('div')(({ theme }) =>
                theme.unstable_sx({
                    width: `48px`,
                    height: `48px`,
                    backgroundColor: palette.Grey100,
                    borderRadius: '50px',
                }),
            ),
        [palette.Grey100],
    )

    const { getRootProps, getInputProps } = useDropzone({ onDrop: onUploadLogo })

    const uploadImage = useMemo(() => {
        if (uploading) {
            return <Loading />
        }

        if (activeOrg?.logo) {
            return <Image src={activeOrg.logo} />
        }

        return <Placeholder />
    }, [uploading, activeOrg, Placeholder])

    return (
        <Container
            disableGutters
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <ListItemLayout
                image={uploadImage}
                title={`Company logo`}
                subTitle={`JPG, JPEG or PNG up to 200kb`}
            />
            <div {...getRootProps()}>
                <ConditionalTooltip show={!isAuthorised} tooltip={NotAdminTooltip}>
                    <Button variant={'text'} onClick={() => {}} disabled={!isAuthorised}>
                        Upload image
                    </Button>
                </ConditionalTooltip>
                <input
                    {...getInputProps()}
                    data-testid={'drop-input'}
                    style={{
                        display: `none`,
                    }}
                />
            </div>
        </Container>
    )
}

export default ManageLogo
